import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material'
import { DialogCloseSaveButtons } from 'components/Common/DialogActionButtons/DialogCloseSaveButtons'
import { ApiErrors } from 'customHooks/useAppController'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ManagementPartyDto,
  PartySCSNDetailsDto,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { ShowException } from 'store/Application/appActions'
import { DashboardManagement } from './DashboardManagement'
import { NewFormParties } from './NewFormParties'
import { SCSNProvisioningForm } from './SCSNProvisioningForm'

type Props = {
  open: boolean
  data: ManagementPartyDto
  onConfirm: (party: ManagementPartyDto) => Promise<ManagementPartyDto>
  onSaveSCSNData: (scsn: PartySCSNDetailsDto) => Promise<void>
  onProvisionSCSN: (scsn: PartySCSNDetailsDto) => Promise<void>
  onDeProvisionSCSN: () => Promise<void>
  isNew: boolean
  onClose: () => void
  onCancel?: () => void
  errors?: {
    validation?: {
      id?: string
      name?: string
      coCKey?: string
      currencyCode?: string
      modules?: string
    }
  }
  scsnLoading: Record<string, boolean>
  scsnErrors: Record<string, ApiErrors>
}

export const SystemPartySettingsDialog = (props: Props) => {
  const { t } = useTranslation()

  const [changedParty, setChangedParty] = useState<ManagementPartyDto>(
    props.data,
  )

  useEffect(() => {
    setChangedParty(props.data)
  }, [props.data])

  const handleCancel = () => {
    props.onCancel?.()
    props.onClose()
  }

  const handleChange = <K extends keyof ManagementPartyDto>(
    key: K,
    value: ManagementPartyDto[K],
  ) => {
    setChangedParty({ ...changedParty, [key]: value })
  }

  const handleSave = async () => {
    try {
      await props.onConfirm(changedParty)
      props.onClose()
    } catch (err) {
      ShowException('party management', err)
    }
  }

  return (
    <Dialog
      open={props.open}
      fullWidth
      maxWidth="lg"
      onClose={(_, reason) => {
        if (reason === 'backdropClick') {
          handleCancel()
        }
      }}
    >
      <DialogTitle>
        {props.isNew
          ? t('system:new-party', 'New Party')
          : t('system:edit-party', 'Edit Party')}
      </DialogTitle>

      <DialogContent>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            handleSave()
          }}
          id="system-party-settings-form"
        >
          <NewFormParties
            data={changedParty}
            onChange={handleChange}
            isNew={props.isNew}
            errors={props.errors}
          />
        </form>
        <div>
          <SCSNProvisioningForm
            party={changedParty}
            onChange={handleChange}
            onSaveSCSNData={props.onSaveSCSNData}
            onProvision={props.onProvisionSCSN}
            onDeProvision={props.onDeProvisionSCSN}
            loading={props.scsnLoading}
            errors={props.scsnErrors}
          />
        </div>
        <Box
          sx={{
            pt: 2,
          }}
        >
          <DashboardManagement partyId={props.data.id} />
        </Box>
      </DialogContent>
      <DialogActions>
        <DialogCloseSaveButtons
          onCloseButtonClicked={handleCancel}
          saveButtonFormId="system-party-settings-form"
        />
      </DialogActions>
    </Dialog>
  )
}
